'use client';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { ListingService } from '@/hotdesk-ui/services/listing.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';

// Per-city Popular/Explore carousels gated by a Remote Config list, used as
// supplemental content under the location-based ones on the landing page.
//
// Remote Config shape (WEB_LANDING_FALLBACK_CAROUSELS):
//   [{ label: string, country: string, lat: number, lng: number }]
//
// Each entry produces one carousel; the listing query reuses the existing
// lat/lng/radius search so no API extension is needed.

export interface FallbackCarouselConfig {
    label: string;
    country: string;
    lat: number;
    lng: number;
}

export interface FallbackCarouselBucket extends FallbackCarouselConfig {
    listings: ReadOnlyListing[];
}

interface FallbackCarouselsState {
    items: FallbackCarouselBucket[];
    loading: boolean;
    error: string | null;
}

const initialState: FallbackCarouselsState = {
    items: [],
    loading: false,
    error: null
};

// Wider radius than the default landing search so we catch country-wide
// results from a single representative city centroid.
const FALLBACK_SEARCH_RADIUS_KM = 50;
const FALLBACK_LIMIT = 12;

export const fetchFallbackCarousels = createAsyncThunk<
    FallbackCarouselBucket[],
    string | undefined
>('fallbackCarousels/fetch', async (locale) => {
    const config = (await fetchRemoteConfig(
        RemoteConfigKeys.WEB_LANDING_FALLBACK_CAROUSELS,
        'object'
    )) as FallbackCarouselConfig[] | null;

    if (!Array.isArray(config) || config.length === 0) return [];

    const apiConfig = await new ApiConfigration().getApiConfig(locale || 'en');
    const service = new ListingService(apiConfig);

    const results = await Promise.all(
        config.map(async (entry) => {
            try {
                const res = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.workspace,
                        FALLBACK_LIMIT,
                        0,
                        {
                            lat: entry.lat,
                            lng: entry.lng,
                            radius: FALLBACK_SEARCH_RADIUS_KM
                        }
                    )
                );
                return {
                    ...entry,
                    listings: (res?.results || []) as ReadOnlyListing[]
                };
            } catch (error) {
                console.error(
                    'fallbackCarousels: failed to fetch',
                    entry.country,
                    error
                );
                return { ...entry, listings: [] as ReadOnlyListing[] };
            }
        })
    );

    return results.filter((r) => r.listings.length > 0);
});

const fallbackCarouselsSlice = createSlice({
    name: 'fallbackCarousels',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchFallbackCarousels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFallbackCarousels.fulfilled, (state, action) => {
                state.items = action.payload;
                state.loading = false;
            })
            .addCase(fetchFallbackCarousels.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.error.message ||
                    'Failed to fetch fallback carousels';
            });
    }
});

export default fallbackCarouselsSlice.reducer;
